import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  /**
   * Optional supporting line (LF-5985).
   *
   * Every consumer before the leads board passed one, and it stays the norm —
   * a title alone rarely tells someone what to do next. It is optional only
   * for a surface whose empty copy is specified exactly and would be *weakened*
   * by an invented second sentence (the leads board's `functional-spec.md` §3.8
   * table). Omitting it renders no second paragraph at all rather than an empty
   * one.
   */
  description?: string;
  /**
   * Draw the state at its smaller size (VALE-1494).
   *
   * The default is sized to be the only thing on a page or in a card — a
   * whole surface with nothing in it yet. `dense` is for a state that is one
   * of SEVERAL on the same screen: the AI review's inventory card holds four
   * groups, up to three of which can be empty at once, and three
   * full-size states there filled a screen with furniture saying nothing.
   *
   * It shrinks the padding and the glyph and leaves the type sizes alone, so a
   * dense state still reads as the same object as its full-size sibling rather
   * than as a different component.
   */
  dense?: boolean;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  dense = false
}: EmptyStateProps) {
  return (
    <div
      data-testid="empty-state"
      className={cn(
        'flex flex-col items-center text-center',
        dense ? 'gap-1 px-4 py-4' : 'gap-1.5 px-4 py-8'
      )}
    >
      <Icon
        className={cn('text-foreground-subtle', dense ? 'h-4 w-4' : 'h-6 w-6')}
      />
      <p className="text-muted-foreground text-sm font-medium">{title}</p>
      {!!description && (
        <p className="text-muted-foreground text-xs">{description}</p>
      )}
    </div>
  );
}
